import axios from "axios";
import {
  resetPricesAndStock,
  resetSelectedProducts,
} from "intranet/features/eCommerce/eCommerceSlice";
import {
  getCategories,
  reset as resetMms,
  SearchProducts,
  setSearchParams as setMmsSearchParams,
} from "intranet/features/MMS/MMSSlice";
import cookieService from "intranet/features/users/cookieService";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getServerApiUrl } from "shared";
import SpinnerWithText from "shared/welda/components/loaders/SpinnerWithText";
import { setDossierHeader } from "../../../services/axiosClient";
export const DossierContext = createContext(null);

function mergeLockedSearch(params, lockedSearchKey) {
  if (!lockedSearchKey) {
    return params;
  }
  return { ...params, ...JSON.parse(lockedSearchKey) };
}

export const DossierProvider = ({
  children,
  dossierNr,
  baseProductsUrl,
  renderLoaderWhileLoading = false,
  blockChildrenUntilLoaded = true,
  getCategoriesOnMount = true,
  lockedSearchParams = null,
}) => {
  const dispatch = useDispatch();
  const mmsState = useSelector((state) => state.mms);
  const { client } = useSelector((state) => state.users);
  const [searchParams, setSearchParams] = useSearchParams();
  const [dossierData, setDossierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const prevLangRef = useRef(null);
  const isDev = process.env.REACT_APP_ENV === "development";
  const lockedSearchKey = lockedSearchParams
    ? JSON.stringify(lockedSearchParams)
    : "";

  //NOTE: The site cannot work without dossierNr context, and thus also not without a minimal cookie with a dossierNr
  //NOTE: This is only needed in development mode, because the cookie is set on first request in production
  const [hasCookie, setHasCookie] = useState(false);

  // Clear product list + prices when switching dossier so stale CAC/MMS data
  // from another dossier is never shown under the new one.
  useEffect(() => {
    if (!dossierNr) {
      return;
    }
    setDossierData(null);
    setError(null);
    dispatch(resetPricesAndStock());
    dispatch(resetSelectedProducts());
    dispatch(resetMms());
    if (lockedSearchKey) {
      dispatch(
        setMmsSearchParams({
          ...JSON.parse(lockedSearchKey),
          forceSearch: true,
        })
      );
    }
  }, [dossierNr, dispatch, lockedSearchKey]);

  useEffect(() => {
    setHasCookie(false);
    const fetchCookie = async () => {
      setHasCookie(false);
      await cookieService.getCookie(dossierNr);
      const checkCookieData = await cookieService.checkCookie();
      if (checkCookieData.success) {
        setHasCookie(true);
      }
    };
    if (isDev) {
      fetchCookie();
    } else {
      setHasCookie(true);
    }
  }, [dossierNr, client]);

  useEffect(() => {
    if (!hasCookie) {
      return;
    }
    const fetchDossierData = async () => {
      try {
        setLoading(true);
        setError(null);
        setDossierHeader(dossierNr);

        if (getCategoriesOnMount) {
          dispatch(getCategories({ dossier: dossierNr }));
        }

        const response = await axios.get(
          `${getServerApiUrl()}/dossier/${dossierNr}`,
          { withCredentials: true }
        );
        setDossierData({ ...response.data, number: dossierNr });
      } catch (err) {
        console.error("Error fetching dossierNr data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (dossierNr) {
      fetchDossierData();
    } else {
      setLoading(false);
    }
  }, [dossierNr, hasCookie, client]);

  useEffect(() => {
    if (!hasCookie) {
      return;
    }
    if (mmsState.forceSearch) {
      dispatch(
        SearchProducts({
          ...mergeLockedSearch(mmsState.searchParams, lockedSearchKey),
          to: "products",
          addStockDetail: true,
        })
      );
      // NOTE: A new search starts at page 1. Replace, never push: a pushed copy of
      // the same URL broke back/forward (and truncated forward history on reload).
      if (searchParams.has("page")) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("page");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [mmsState.searchParams, hasCookie, lockedSearchKey]);

  useEffect(() => {
    if (!hasCookie) {
      return;
    }
    const shouldSearch = !Object.values(mmsState.searchParams_temp).every(
      (x) => {
        return (
          x === null ||
          x === "" ||
          x === true ||
          x === false ||
          (typeof x === "object" && x !== null && Object.keys(x).length === 0)
        );
      }
    );
    if (shouldSearch && mmsState.forceSearch) {
      dispatch(
        SearchProducts({
          ...mergeLockedSearch(mmsState.searchParams_temp, lockedSearchKey),
          to: "products_temp",
        })
      );
    }
    if (!shouldSearch) {
      //dispatch(reset());
    }
  }, [mmsState.searchParams_temp, hasCookie, currentLang, lockedSearchKey]);

  // Refetch catalog when language changes (MMS product copy is language-specific).
  useEffect(() => {
    if (!hasCookie) {
      return;
    }
    if (prevLangRef.current === null) {
      prevLangRef.current = currentLang;
      return;
    }
    if (prevLangRef.current === currentLang) {
      return;
    }
    prevLangRef.current = currentLang;
    dispatch(
      SearchProducts({
        ...mergeLockedSearch(mmsState.searchParams, lockedSearchKey),
        to: "products",
        addStockDetail: true,
      })
    );
  }, [currentLang, hasCookie, dispatch, mmsState.searchParams, lockedSearchKey]);

  const value = {
    dossier: dossierData ?? { number: dossierNr },
    baseProductsUrl,
    loading,
    error,
  };

  if (error) return <div>Error loading dossierNr: {error}</div>;

  const shouldBlockChildren =
    blockChildrenUntilLoaded && loading && !renderLoaderWhileLoading;

  return (
    <DossierContext.Provider value={value}>
      {!hasCookie && isDev ? null : renderLoaderWhileLoading && loading ? (
        <SpinnerWithText text="Loading products..." />
      ) : shouldBlockChildren ? null : (
        children
      )}
    </DossierContext.Provider>
  );
};

// Custom hook to use the dossierNr context
export const useDossier = () => {
  const context = useContext(DossierContext);
  if (context == null) {
    // Outside DossierProvider (e.g. intranet planning) — callers should use optional chaining.
    return null;
  }
  return context;
};
